
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Phone, Mail, MapPin, Calendar, AlertTriangle, CreditCard, Weight, Ruler } from 'lucide-react';
import { Client } from '@/models/Client';
import { useTranslation } from 'react-i18next';
import { calculateAge, formatDateWithDay } from '@/utils/dateHelpers';
import ClientNotesEditor from './ClientNotesEditor';

interface ClientDetailsHeaderProps {
  client: Client;
}

const ClientDetailsHeader: React.FC<ClientDetailsHeaderProps> = ({ client }) => {
  const { t, i18n } = useTranslation();
  const age = calculateAge(client.birthDate);

  const isUnpaid = client.paymentStatus === 'unpaid' || client.paymentStatus === 'partial';
  const hasLowSessions = client.subscriptions.some(sub => 
    sub.isActive && (sub.sessionsPerMonth - sub.currentCycleSessionsUsed) <= 1
  );

  return (
    <Card>
      <CardHeader className="pb-3 md:pb-6">
        <div className="flex flex-col gap-3 md:gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 md:h-6 md:w-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-lg md:text-xl lg:text-2xl greek-text truncate">
                {client.firstName} {client.lastName}
              </CardTitle>
              <p className="text-xs md:text-sm text-muted-foreground">
                {t('clients.clientSince')} {formatDateWithDay(client.registrationDate, i18n.language)}
              </p>
            </div>
          </div>
          
          {/* Badges - Mobile optimized */}
          <div className="flex flex-wrap gap-1.5 md:gap-2">
            {isUnpaid && (
              <Badge variant="destructive" className="flex items-center gap-1 text-xs">
                <CreditCard className="h-3 w-3" />
                {t(`clients.${client.paymentStatus}`)}
              </Badge>
            )}
            {hasLowSessions && (
              <Badge variant="outline" className="flex items-center gap-1 border-orange-500 text-orange-600 text-xs">
                <AlertTriangle className="h-3 w-3" />
                {t('clients.lowSessions')}
              </Badge>
            )}
            <Badge variant={client.isActive ? 'default' : 'secondary'} className="text-xs">
              {client.isActive ? t('common.active') : t('common.inactive')}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {client.phone && (
            <div className="flex items-center gap-2 text-xs md:text-sm">
              <Phone className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
              <a href={`tel:${client.phone}`} className="text-primary hover:underline truncate">
                {client.phone}
              </a>
            </div>
          )}
          
          {client.email && (
            <div className="flex items-center gap-2 text-xs md:text-sm">
              <Mail className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
              <a href={`mailto:${client.email}`} className="text-primary hover:underline truncate">
                {client.email}
              </a>
            </div>
          )}
          
          {client.address && (
            <div className="flex items-center gap-2 text-xs md:text-sm">
              <MapPin className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{client.address}</span>
            </div>
          )}
          
          {age && (
            <div className="flex items-center gap-2 text-xs md:text-sm">
              <Calendar className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
              <span>{age} {t('common.yearsOld')}</span>
            </div>
          )}
          
          {client.weightKg && (
            <div className="flex items-center gap-2 text-xs md:text-sm">
              <Weight className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
              <span>{client.weightKg} kg</span>
            </div>
          )}
          
          {client.heightCm && (
            <div className="flex items-center gap-2 text-xs md:text-sm">
              <Ruler className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
              <span>{client.heightCm} cm</span>
            </div>
          )}
        </div>
        
        <ClientNotesEditor client={client} />
      </CardContent>
    </Card>
  );
};

export default ClientDetailsHeader;
